import { SubscriptionTier } from '@upright/common/activities/contact-forms/company-subscription/subscription-tiers';
import {
  SubscriptionRequest,
  SubscriptionResponse,
} from '@upright/common/activities/contact-forms/company-subscription/types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { getGoogleAnalyticsCookieValue } from '@upright/ui/common/generic/utilities/get-google-analytics-cookie-value';
import { toUpper } from 'lodash';
import { useMutation } from '@tanstack/react-query';
import { submitCompanyPlatformSubscriptionToHubspot } from './submit-company-platform-subscription';

export function useSubmitSubsriptionForm(onSuccess: () => void) {
  return useMutation({
    mutationKey: ['submitSubscriptionForm'],

    mutationFn: async (request: SubscriptionRequest) => {
      const requestWithGACookie = { ...request, gaClientId: getGoogleAnalyticsCookieValue() };
      return await submitSubscriptionForm(requestWithGACookie);
    },

    onSuccess,
  });
}

async function submitSubscriptionForm(request: SubscriptionRequest): Promise<SubscriptionResponse> {
  let hubSpotSuccess = false;
  let serverResponse: SubscriptionResponse | undefined = undefined;
  let error: Error | undefined = undefined;

  const requestWithoutPassword = {
    ...request,
    password: request.password ? 'password hidden from logs' : undefined,
  };

  try {
    serverResponse = await fetchOrThrow<SubscriptionResponse>('/company-subscription', 'POST', request);
  } catch (err) {
    console.error('Failed to submit subscription to server', requestWithoutPassword, err);
    error = err as Error;
  }

  // Free tier subscriptions create a user account, so they'll have to go through our server and can't go directly
  // to Hubspot
  if (request.tier === SubscriptionTier.FREE) {
    if (serverResponse) return serverResponse;
    if (error) throw error;
    throw Error(
      'No server response nor error with free subscription. This is unexpected, please contact the Upright team.',
    );
  }
  try {
    await submitCompanyPlatformSubscriptionToHubspot(request);
    hubSpotSuccess = true;
  } catch (err) {
    console.error('Failed to submit subscription to hubspot', requestWithoutPassword, err);
  }

  if (hubSpotSuccess || serverResponse) {
    return { success: true };
  }

  throw Error(`Failed to store subscription`);
}

export function useValidatePromoCode() {
  return useMutation({
    mutationKey: ['submitSubscriptionForm'],

    mutationFn: async (request: { code: string }) => {
      return await fetchValidatePromoCode(request);
    },
  });
}

const validPromoCodes = ['EARLYBIRD'];

async function fetchValidatePromoCode(request: { code: string }): Promise<{ code: string }> {
  await new Promise(resolve => setTimeout(resolve, 1000));
  if (!validPromoCodes.includes(toUpper(request.code))) {
    throw Error('Invalid code!');
  }
  return { code: request.code };
}
